import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { WebSocketService } from '@/shared/services/websocketService'

type ConnectionState = 'connecting' | 'connected' | 'disconnected' | 'reconnecting'

interface WebSocketState {
  // Connection state
  connectionState: ConnectionState
  lastConnectedAt: number | null
  lastDisconnectedAt: number | null
  reconnectAttempt: number
  
  // Feature-specific real-time data
  activeTimers: Map<string, {
    userId: string
    userName: string
    projectId: string
    taskId: string | null
    description: string
    elapsedSeconds: number
    startedAt: number
  }>
  
  teamMemberStatuses: Map<string, {
    userId: string
    userName: string
    status: 'online' | 'away' | 'offline' | 'active'
    lastSeenAt: number
    currentTimer: {
      projectId: string
      taskId: string | null
      description: string
      startedAt: number
    } | null
  }>
  
  recentTimeEntries: Array<{
    id: string
    userId: string
    userName: string
    projectId: string
    taskId: string | null
    description: string
    duration: number
    createdAt: number
  }>
  
  // Service reference (not persisted)
  service: WebSocketService | null
  
  // Actions
  setConnectionState: (state: ConnectionState) => void
  setService: (service: WebSocketService | null) => void
  recordConnected: () => void
  recordDisconnected: () => void
  setReconnectAttempt: (attempt: number) => void
  
  // Feature actions
  addActiveTimer: (timer: WebSocketState['activeTimers'] extends Map<string, infer V> ? V : never) => void
  removeActiveTimer: (userId: string) => void
  updateActiveTimer: (userId: string, elapsedSeconds: number) => void
  
  updateTeamMemberStatus: (status: WebSocketState['teamMemberStatuses'] extends Map<string, infer V> ? V : never) => void
  removeTeamMember: (userId: string) => void
  
  addRecentTimeEntry: (entry: WebSocketState['recentTimeEntries'][number]) => void
  updateTimeEntry: (id: string, updates: Partial<WebSocketState['recentTimeEntries'][number]>) => void
  removeTimeEntry: (id: string) => void
  clearRecentTimeEntries: () => void
}

export const useWebSocketStore = create<WebSocketState>()(
  persist(
    (set, get) => ({
      // Initial state
      connectionState: 'disconnected',
      lastConnectedAt: null,
      lastDisconnectedAt: null,
      reconnectAttempt: 0,
      activeTimers: new Map(),
      teamMemberStatuses: new Map(),
      recentTimeEntries: [],
      service: null,
      
      // Connection actions
      setConnectionState: (state) => set({ connectionState: state }),
      
      setService: (service) => set({ service }),
      
      recordConnected: () => set({
        lastConnectedAt: Date.now(),
        reconnectAttempt: 0,
      }),
      
      recordDisconnected: () => set({
        lastDisconnectedAt: Date.now(),
      }),
      
      setReconnectAttempt: (attempt) => set({ reconnectAttempt: attempt }),
      
      // Timer actions
      addActiveTimer: (timer) => {
        const activeTimers = new Map(get().activeTimers)
        activeTimers.set(timer.userId, timer)
        set({ activeTimers })
      },
      
      removeActiveTimer: (userId) => {
        const activeTimers = new Map(get().activeTimers)
        activeTimers.delete(userId)
        set({ activeTimers })
      },
      
      updateActiveTimer: (userId, elapsedSeconds) => {
        const activeTimers = new Map(get().activeTimers)
        const timer = activeTimers.get(userId)
        if (timer) {
          activeTimers.set(userId, { ...timer, elapsedSeconds })
          set({ activeTimers })
        }
      },
      
      // Team member actions
      updateTeamMemberStatus: (status) => {
        const teamMemberStatuses = new Map(get().teamMemberStatuses)
        teamMemberStatuses.set(status.userId, status)
        set({ teamMemberStatuses })
      },
      
      removeTeamMember: (userId) => {
        const teamMemberStatuses = new Map(get().teamMemberStatuses)
        teamMemberStatuses.delete(userId)
        set({ teamMemberStatuses })
      },
      
      // Time entry actions
      addRecentTimeEntry: (entry) => {
        const recentTimeEntries = [entry, ...get().recentTimeEntries].slice(0, 50) // Keep last 50
        set({ recentTimeEntries })
      },
      
      updateTimeEntry: (id, updates) => {
        const recentTimeEntries = get().recentTimeEntries.map(entry =>
          entry.id === id ? { ...entry, ...updates } : entry
        )
        set({ recentTimeEntries })
      },
      
      removeTimeEntry: (id) => {
        const recentTimeEntries = get().recentTimeEntries.filter(entry => entry.id !== id)
        set({ recentTimeEntries })
      },
      
      clearRecentTimeEntries: () => set({ recentTimeEntries: [] }),
    }),
    {
      name: 'websocket-storage',
      partialize: (state) => ({
        // Only persist connection metadata, not the service instance
        lastConnectedAt: state.lastConnectedAt,
        lastDisconnectedAt: state.lastDisconnectedAt,
        reconnectAttempt: state.reconnectAttempt,
      }),
    }
  )
)

export default useWebSocketStore
